import Colors from '@constants/Colors';
import { Card, IconButton, Stack, Box, CircularProgress, Menu, MenuItem, Typography, useTheme, Theme } from '@mui/material';
import { fr } from 'date-fns/locale';
import React, { useState } from 'react'
import { Link } from 'react-router-dom';
import BlogStatus from './BlogStatus';
import { format } from 'date-fns';
import { get } from 'lodash';

type Props = {
    data: any,
    isLoading: boolean,
    handleOpenDialog: (id: string) => void,
    updateBlogStatusMutate: (id: string, status: string) => Promise<void>, // <-- add this line
    readOnly?: boolean,
};

const BlogCard = ({
    data,
    isLoading,
    handleOpenDialog,
    updateBlogStatusMutate, // <-- add this line
    readOnly,
}: Props) => {
    const theme = useTheme();
    const [isHovered, setIsHovered] = useState(false);
    // State for status menu
    const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
    const [statusMenuItem, setStatusMenuItem] = useState<any>(null);

    function getBorderStyle(data: any, theme: Theme) {
        if (data.isPinned) return `1px solid ${Colors.PRIMARY_PURPLE}`;
        return theme.palette.mode === 'light'
            ? '1px solid rgba(0, 0, 0, 0.12)'
            : '1px solid rgba(255, 255, 255, 0.10)';
    }

    function getBackgroundStyle(data: any, theme: Theme) {
        if (data.isPinned) {
            if (data.banner?.url) {
                if (theme.palette.mode === 'light') {
                    return `linear-gradient(${Colors.PRIMARY_PURPLE}40 , ${Colors.PRIMARY_PURPLE}40 ), url(${data.banner.url}) center/cover no-repeat`;
                }
                return `linear-gradient(${Colors.PRIMARY_PURPLE}40 , ${Colors.PRIMARY_PURPLE}40 ), url(${data.banner.url}) center/cover no-repeat`;
            }
            if (theme.palette.mode === 'light') {
                return `linear-gradient(180deg, ${Colors.PRIMARY_PURPLE}, ${Colors.PRIMARY_PURPLE})`;
            }
            return `linear-gradient(180deg, ${Colors.PRIMARY_PURPLE}40 50%, ${Colors.PRIMARY_PURPLE}40 50%)`;
        }
        if (data.banner?.url) {
            return `linear-gradient(rgba(12,13,24,0.7), rgba(12,13,24,0.7)), url(${data.banner.url}) center/cover no-repeat`;
        }
        return theme.palette.mode === 'light'
            ? "#ffffff"
            : "var(--background-bar, #0C0D18)";
    }

    function getBoxShadowStyle(theme: Theme) {
        return theme.palette.mode === 'light'
            ? "0px 2px 8px rgba(0, 0, 0, 0.1)"
            : "15px 15px 19px -2px rgba(81, 75, 75, 0.06)";
    }

    function getBoxShadowHoverStyle(theme: Theme) {
        return theme.palette.mode === 'light'
            ? "0px 4px 16px rgba(0, 0, 0, 0.15)"
            : "15px 15px 25px -2px rgba(81, 75, 75, 0.12)";
    }

    function getPinIconStyle(data: any, theme: Theme) {
        if (data.isPinned) {
            if (theme.palette.mode === 'light') {
                return Colors.WHITE;
            }
            return Colors.PRIMARY_PURPLE;   
        };
        if (theme.palette.mode === 'light' && !data.banner?.url) {
            return Colors.NEUTRAL_BLACK
        }
        return Colors.WHITE;
    }

    function getBlogStatusStyle(data: any, theme: Theme) {
        if (data.isPinned) {
            if (theme.palette.mode === 'light') {
                return Colors.WHITE;
            }
        }
        if (theme.palette.mode === 'light') {
            return "#F9f9f9"
        }

        return "var(--background-Secondary, #282C30)";
    }

    function getTitleStyle(data: any, theme: Theme) {
        if (data.isPinned) {
            if (theme.palette.mode === 'light') {
                return Colors.WHITE;
            }
        }
        if (theme.palette.mode === 'light' && !data.banner?.url) { 
            return "#0C0D18"
        }
        return "var(--text-primary, #FFF)"
    }

    function getAuthorName(authorName: any, 
        date: any, 
        isUpdated: boolean = false, 
        isPinned: boolean = false,
        bannerUrl: string | undefined = undefined) {
        return (
            <Typography variant="body2" color="text.primary" fontWeight={700}>
                <Typography component="span" 
                sx={{color: `${Colors.DROPDOWN_FLOATING_TEXT} !important`}} 
                fontWeight={700}>{isUpdated ? 'Modifié par : ' : 'Créé par : '}
                </Typography> 
                <Typography component="span"
                 sx={
                    {
                        color: `${theme.palette.mode === 'light' ? 
                        (isPinned || bannerUrl ? Colors.WHITE : Colors.FLOATING_BAR_TEXT) : Colors.WHITE} !important`
                    }
                }
                 fontWeight={700}
                 >{authorName}</Typography> <Typography component="span" sx={{color: `${Colors.DROPDOWN_FLOATING_TEXT} !important`}} fontWeight={700}>le</Typography>
                
                <Typography component="span" 
                sx={{color: `${theme.palette.mode === 'light' ? `${Colors.DROPDOWN_FLOATING_TEXT} !important` : Colors.WHITE}`}} 
                fontWeight={700}> {format(new Date(date), 'MMM dd, yyyy', { locale: fr })}</Typography>
            </Typography>
        )
    }

    return (
        <Card
            sx={{
                height: '180px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                cursor: 'pointer',
                borderRadius: "8px",
                border: getBorderStyle(data, theme),
                background: getBackgroundStyle(data, theme),
                boxShadow: getBoxShadowStyle(theme),
                textDecoration: 'none',
                position: 'relative',
                '&:hover': {
                    boxShadow: getBoxShadowHoverStyle(theme),
                    transform: 'translateY(-2px)',
                    transition: 'all 0.3s ease',
                }
            }}
            component={readOnly ? 'div' : Link}
            to={readOnly ? undefined : `/blog/${data._id}/edit`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Pin and Delete buttons */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '8px',
                    zIndex: 2,
                }}
                onClick={e => { e.preventDefault(); e.stopPropagation(); }}
            >
                {!readOnly && (isHovered || data.isPinned) && (
                    <IconButton
                        aria-label="pin"
                        size="small"
                        sx={{
                            width: "26px",
                            height: "26px",
                            padding: "12px",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: "24px",
                            transition: 'background 0.3s',
                        }}
                        onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log(data._id, data.isPinned ? 'unpinned' : 'pinned');
                            await updateBlogStatusMutate(data._id, data.isPinned ? 'unpinned' : 'pinned');
                        }}
                        disabled={isLoading}
                    >
                        <i
                            className={`fi fi-br-thumbtack ${data.isPinned ? 'pinned' : ''}`}
                            style={{
                                color: getPinIconStyle(data, theme),
                                fontSize: '20px',
                                transition: 'color 0.3s ease',
                            }}
                        ></i>
                    </IconButton>
                )}
                {!readOnly && (
                <IconButton
                    aria-label="delete"
                    size="small"
                    sx={{
                        width: "26px",
                        height: "26px",
                        padding: "12px",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "24px",
                        background: "var(--Color-Error-Bold, #E45858)",
                    }}
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleOpenDialog(data._id);
                    }}
                >
                    <i className='fi fi-br-trash' style={{ fontSize: '14px', color: Colors.WHITE }}></i>
                </IconButton>
                )}
            </Box>
            <Stack sx={{ p: 2, height: '100%' }}>
                <Box
                    sx={{
                        display: "flex",
                        padding: "var(--spacing-5, 4px) 12px",
                        alignItems: "center",
                        gap: "4px",
                        borderRadius: "16px",
                        background: getBlogStatusStyle(data, theme),
                        backdropFilter: "blur(2.799999952316284px)",
                        marginRight: 'auto',
                        width: 'fit-content',
                        cursor: 'pointer',
                        height: '24px',
                    }}
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (readOnly) {
                            return;
                        }
                        setStatusMenuAnchor(e.currentTarget);
                        setStatusMenuItem(data);
                    }}
                >
                    {isLoading && <CircularProgress size={15} />}
                    <BlogStatus isPublished={data.isPublished} isDraft={data.isDraft} />
                </Box>
                {!readOnly && (
                <Menu
                    anchorEl={statusMenuAnchor}
                    open={Boolean(statusMenuAnchor) && statusMenuItem?._id === data._id}
                    onClose={() => setStatusMenuAnchor(null)}
                    sx={{
                        "& .MuiMenu-list": {
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                        },
                        "& .MuiMenuItem-root": {
                            color: "var(--Neutral-White, #FFF)",
                            textAlign: "center",
                            fontFamily: "var(--font-family-Core, Plus Jakarta Sans)",
                            fontSize: "var(--font-size-md, 16px)",
                            fontStyle: "normal",
                            fontWeight: 700,
                            lineHeight: "var(--font-line-height-sm, 24px)",
                            padding: "12px 10px",
                            "&:hover": {
                                borderRadius: "var(--Radius-radius-minimal, 8px)",
                                background: theme.palette.mode === 'light' ? 
                                "var(--Neutral-Light-Gray, #F9F9F9)" : "var(--Color-Neutral-Dashboard-100, #222426)",
                            }
                        },
                        "& .MuiPaper-root": {
                            minWidth: "200px",
                            marginTop: "16px",
                            borderRadius: "16px",
                            padding: "12px",
                            border: `1px solid ${theme.palette.mode === 'light' ? 'var(--Neutral-White, #FFF)': 'var(--Dasbhoard, #222426)'}`,
                            background: theme.palette.mode === 'light' ? "var(--Neutral-White, #FFF)" : "var(--Disable-form-Black, #282C30)",
                            boxShadow: "0px 16px 40px 4px rgba(18, 31, 44, 0.80)",
                        },
                    }}
                >

                    <MenuItem
                        onClick={async (e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setStatusMenuAnchor(null);
                            console.log(data._id, 'archived');
                            await updateBlogStatusMutate(data._id, 'unpublished');
                        }}
                        disabled={!data.isPublished}
                    >
                        Dépublier
                    </MenuItem>
                    <MenuItem
                        onClick={async (e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setStatusMenuAnchor(null);
                            console.log(data._id, 'published');
                            await updateBlogStatusMutate(data._id, 'published');
                        }}
                        disabled={data.isPublished}
                    >
                        Publier
                    </MenuItem>
                    <MenuItem
                        onClick={async (e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setStatusMenuAnchor(null);
                            console.log(data._id, 'draft');
                            await updateBlogStatusMutate(data._id, 'draft');
                        }}
                        disabled={data.isDraft}
                    >
                        Mettre en brouillon
                    </MenuItem>
                </Menu>
                )}
                <Typography
                    variant="h6"
                    fontWeight={700}
                    color={getTitleStyle(data, theme)}
                    fontFamily="Lemon milk pro"
                    fontSize="16px"
                    lineHeight="24px"
                    gutterBottom
                    sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        padding: 0,
                        margin: 'auto 0',
                    }}
                >
                    {data.title}
                </Typography>
                {data.updatedBy?.name && data.updatedAt ? 
                getAuthorName(data.updatedBy.name, data.updatedAt, true, data?.isPinned, data.banner?.url) : 
                getAuthorName(data.createdBy.name, data.createdAt, false, data?.isPinned, data.banner?.url)}
            </Stack>
        </Card>
    )
}

export default BlogCard
